import { useCallback, useEffect, useState } from 'react';
import { logAdminModeEnabled } from '@/utils/activityLogger';

// Admin mode on a merchant's Settings tab (FIG-345 item 7).
//
// - Off by default, and off again every time the admin opens a merchant:
//   the state lives in the merchant page (not in storage), and it is tied to
//   the merchant id, so moving to another merchant starts from off. Switching
//   merchants also clears it, so coming back to a merchant starts from off
//   again and turning it on is logged again (Cursor Bugbot on #279). Tab
//   changes on the same merchant keep it.
// - Only admins can turn it on, and only once their email is known, so that
//   turning it on is always recorded in User Activity (who, when, which
//   merchant). A failed or rejected log never blocks the switch; it is
//   reported to the console.

export interface AdminModeOptions {
  isAdmin: boolean;
  userEmail?: string | null;
  userId?: string;
  organisationName?: string | null;
}

export interface AdminModeState {
  /** True only for an admin who turned admin mode on for this merchant. */
  adminMode: boolean;
  /** False until the viewer is a known admin with an email to record. */
  canEnable: boolean;
  setAdminMode: (enabled: boolean) => void;
}

export function useAdminMode(organisationId: string | undefined, options: AdminModeOptions): AdminModeState {
  const { isAdmin, userEmail, userId, organisationName } = options;
  const [state, setState] = useState<{ organisationId?: string; enabled: boolean }>({ enabled: false });

  // A merchant switch turns admin mode off; returning to the previous merchant doesn't restore it.
  useEffect(() => {
    setState((current) => (current.organisationId === organisationId ? current : { organisationId, enabled: false }));
  }, [organisationId]);

  const canEnable = isAdmin && !!organisationId && !!userEmail;
  const adminMode = isAdmin && !!organisationId && state.organisationId === organisationId && state.enabled;

  const setAdminMode = useCallback((enabled: boolean) => {
    if (!organisationId) return;
    if (!enabled) {
      setState({ organisationId, enabled: false });
      return;
    }
    if (!isAdmin) return;
    if (!userEmail) {
      console.warn('[useAdminMode] Admin mode not turned on: the signed-in user has no email to record yet');
      return;
    }
    setState({ organisationId, enabled: true });
    logAdminModeEnabled(userEmail, organisationId, organisationName ?? '', userId)
      .then((recorded) => {
        if (!recorded) console.error('[useAdminMode] Admin mode was turned on but could not be recorded');
      })
      .catch((error: unknown) => {
        console.error('[useAdminMode] Admin mode was turned on but recording it failed', error);
      });
  }, [organisationId, isAdmin, userEmail, userId, organisationName]);

  return { adminMode, canEnable, setAdminMode };
}
